import type { Reference, Resource } from '@medplum/fhirtypes'
import type { ReactNode } from 'react'
import { create } from 'zustand'
import { combine, devtools } from 'zustand/middleware'

export type UpsertResourceFormDialogState<T extends Resource = Resource> = {
  isOpen: boolean
  resource: Partial<T> | Reference<T> | null
  title: ReactNode
  onSubmit: ((resource: T) => void) | null
  visibleFields?: (keyof T)[]
  validate?: (resource: T) => Error[]
  transform?: (resource: T) => T
  staticFieldValues?: Partial<T>
}

const initialState: UpsertResourceFormDialogState = {
  isOpen: false,
  resource: null,
  title: '',
  onSubmit: null,
  visibleFields: undefined,
  validate: undefined,
  transform: undefined,
  staticFieldValues: undefined,
}

export const useUpsertResourceFormDialogStore = create(
  devtools(
    combine(initialState, (set) => ({
      open: <T extends Resource = Resource>(config: {
        resource: Partial<T> | Reference<T>
        title: ReactNode
        onSubmit: (resource: T) => void
        visibleFields?: (keyof T)[]
        validate?: (resource: T) => Error[]
        transform?: (resource: T) => T
        staticFieldValues?: Partial<T>
      }) => {
        requestAnimationFrame(() => {
          set({
            isOpen: true,
            resource: config.resource,
            title: config.title,
            onSubmit: config.onSubmit as (resource: Resource) => void,
            visibleFields: config.visibleFields as (keyof Resource)[],
            validate: config.validate as (resource: Resource) => Error[],
            transform:
              config.transform as UpsertResourceFormDialogState['transform'],
            staticFieldValues: config.staticFieldValues as Partial<Resource>,
          })
        })
      },
      setIsOpen: (isOpen: boolean) =>
        set((state) => {
          if (!isOpen) {
            return initialState
          }
          return { ...state, isOpen }
        }),
      reset: () => set(initialState),
    }))
  )
)
